import { Check, CircleX, Info, TriangleAlert, Zap } from "lucide-react";
import React, { ElementType, ReactNode } from "react";

import {
  AlertColor,
  Box,
  ButtonPropsColorOverrides,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { OverridableStringUnion } from "@mui/types";

import { IgButton } from "./IgButton";
import { IgIconButton } from "./IgIconButton";
import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";
import { IgTheme } from "./theme";

type IgAlertSeverity = AlertColor | "recommendation" | "upgrade";

interface IgAlertProps {
  alertKey: string;
  isDismissed?: boolean;
  onDismiss?: () => void;
  dismissAriaLabel?: string;
  severity: IgAlertSeverity;
  title?: string;
  message: ReactNode | string;
  primaryButton?: {
    cta: string;
    onClick: () => void;
    loading?: boolean;
    tooltipMessage?: string;
    disabled?: boolean;
  };
  secondaryButton?: {
    cta: string;
    onClick: () => void;
    loading?: boolean;
    tooltipMessage?: string;
    disabled?: boolean;
  };
  loading?: boolean;
  loadingPercent?: number;
  icon?: React.ReactNode;
}

const IconMap: Record<IgAlertSeverity, ElementType> = {
  error: CircleX,
  warning: TriangleAlert,
  success: Check,
  info: Info,
  recommendation: Zap,
  upgrade: Zap,
};

const theme = IgTheme;

const IconColorMap: Record<IgAlertSeverity, string> = {
  error: theme.igIconColors.error,
  warning: theme.igIconColors.warning,
  info: theme.igIconColors.info,
  success: theme.igIconColors.success,
  recommendation: theme.igPrimitives.base["500"],
  upgrade: theme.igPrimitives.purple["800"],
};

const ColorMap: Record<
  IgAlertSeverity,
  { border: string; background: string }
> = {
  error: {
    border: theme.igBorders.error.default,
    background: theme.igBackgroundColors.tonal.error,
  },
  warning: {
    border: theme.igBorders.warning.default,
    background: theme.igBackgroundColors.tonal.warning,
  },
  info: {
    border: theme.igBorders.info.default,
    background: theme.igBackgroundColors.tonal.info,
  },
  success: {
    border: theme.igBorders.success.default,
    background: theme.igBackgroundColors.tonal.success,
  },
  upgrade: {
    border: theme.igPrimitives.purple["800"],
    background: theme.igPrimitives.purple["50"],
  },
  recommendation: {
    border: theme.igPrimitives.base["500"],
    background: theme.igBackgroundColors.tonal.info,
  },
};

const ButtonColorMap: Record<
  IgAlertSeverity,
  OverridableStringUnion<
    | "inherit"
    | "warning"
    | "error"
    | "success"
    | "primary"
    | "secondary"
    | "info",
    ButtonPropsColorOverrides
  >
> = {
  error: "error",
  upgrade: "upgrade",
  info: "primary",
  recommendation: "primary",
  success: "success",
  warning: "warning",
};

export const IgAlert = ({
  alertKey,
  isDismissed = false,
  message,
  onDismiss,
  dismissAriaLabel = "Dismiss",
  severity,
  title,
  primaryButton,
  secondaryButton,
  loadingPercent,
  loading,
  icon,
}: IgAlertProps) => {
  const Icon: ElementType = IconMap[severity];
  if (isDismissed) {
    return null;
  }

  return (
    <Stack direction={"column"} sx={{ width: "100%" }}>
      <Grid
        id={alertKey}
        direction={"row"}
        container
        columnSpacing={3}
        sx={{
          backgroundColor: ColorMap[severity].background,
          boxShadow: `inset 0 0 0 1px ${ColorMap[severity].border}`,
          borderRadius: "8px",
          padding: "12px 16px",
          textAlign: "left",
        }}
        size={{
          sm: "grow",
        }}
      >
        <Grid container alignItems={"start"} sx={{ mt: 1 }}>
          {icon ? icon : <Icon color={IconColorMap[severity]} size={24} />}
        </Grid>
        <Grid
          alignItems={"center"}
          container
          size={{
            sm: "grow",
          }}
        >
          <Stack spacing={1} direction={"column"}>
            {title && <IgTypography fontWeight={600}>{title}</IgTypography>}
            {typeof message === "string" ? (
              <IgTypography color={theme.palette.text.secondary}>
                {message}
              </IgTypography>
            ) : (
              <span
                style={{
                  color: theme.palette.text.secondary,
                }}
              >
                {message}
              </span>
            )}
          </Stack>
        </Grid>
        <Grid container alignItems={"center"}>
          {loadingPercent && loadingPercent > 0 ? (
            <Grid>
              <IgTypography color={theme.palette.text.secondary}>
                {loadingPercent.toFixed(0)}%
              </IgTypography>
            </Grid>
          ) : null}
          {secondaryButton && (
            <Grid>
              <IgTooltip title={secondaryButton?.tooltipMessage}>
                <IgButton
                  disabled={secondaryButton?.disabled}
                  variant={"text"}
                  loading={secondaryButton?.loading}
                  onClick={() => {
                    secondaryButton?.onClick();
                  }}
                >
                  {secondaryButton.cta}
                </IgButton>
              </IgTooltip>
            </Grid>
          )}
          {primaryButton && (
            <Grid>
              <IgTooltip title={primaryButton?.tooltipMessage}>
                <IgButton
                  disabled={primaryButton?.disabled}
                  color={ButtonColorMap[severity]}
                  loading={primaryButton?.loading}
                  onClick={() => {
                    primaryButton?.onClick();
                  }}
                >
                  {primaryButton.cta}
                </IgButton>
              </IgTooltip>
            </Grid>
          )}
        </Grid>
        {onDismiss && (
          <Grid container alignItems={"start"}>
            <IgIconButton
              size={"small"}
              iconSize={16}
              onClick={onDismiss}
              iconType={"close"}
              aria-label={dismissAriaLabel}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        sx={{
          marginLeft: "4px",
          marginRight: "4px",
          borderRadius: "9px",
          marginTop: "-3px",
        }}
      >
        <Box
          sx={{
            height: "2px",
            opacity: loading ? 1 : 0,
            width: "100%",
            backgroundColor: "rgba(255, 255, 255, 0)",
            borderRadius: "15px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: ColorMap[severity].border,
              height: "100%",
              width: `${loadingPercent}%`,
              ...(loadingPercent === undefined && {
                animation: "indeterminateAnimation 4s infinite linear",
                transformOrigin: "0 50%",
                width: "100%",
              }),
            }}
          />
        </Box>
      </Grid>
    </Stack>
  );
};
